import React from 'react';
import { Box, Slider, Typography, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import { typographyStyles } from '../../../modals/unified_config_modals/styles/unifiedConfigStyles';
import type { RangeThumb } from '@/hooks/pvd_config_hooks/usePvdConfigFields';

interface Props {
  /** Inclusive lower bound for the channel (1 for both voltage and current). */
  channelMin: number;
  /** Inclusive upper bound (22 for voltage, 11 for current). */
  channelMax: number;
  minValue: number;
  maxValue: number;
  /**
   * `activeThumb` identifies which thumb the user is actually dragging, so
   * callers can clamp only that thumb when enforcing min != max, rather
   * than moving the thumb the user isn't touching.
   */
  onChange: (min: number, max: number, activeThumb: RangeThumb) => void;
  disabled?: boolean;
}

const SmartAdaptiveRangeSlider: React.FC<Props> = ({
  channelMin,
  channelMax,
  minValue,
  maxValue,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();

  // MUI's Slider reports the dragged thumb as index 0 (lower) / 1 (upper);
  // translate that into the library-agnostic RangeThumb type at this
  // boundary so no other layer needs to know MUI's numbering.
  const handleChange = (
    _event: Event,
    value: number | number[],
    muiActiveThumb: number,
  ) => {
    if (!Array.isArray(value) || value.length !== 2) return;
    const [lo, hi] = value;
    const activeThumb: RangeThumb = muiActiveThumb === 0 ? 'min' : 'max';
    onChange(lo, hi, activeThumb);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={typographyStyles.subsectionHeading}>
        {t(StringKeys.ADAPTIVE_RANGE_LIMITS)}
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mt: 0.5, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t(StringKeys.SA_MIN_LOWEST)}: {minValue}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(StringKeys.SA_MAX_HIGHEST)}: {maxValue}
        </Typography>
      </Stack>

      <Slider
        value={[minValue, maxValue]}
        onChange={handleChange}
        min={channelMin}
        max={channelMax}
        step={1}
        marks
        disableSwap
        disabled={disabled}
        valueLabelDisplay="auto"
        sx={{ width: '90%', color: 'complete.main' }}
      />
    </Box>
  );
};

export default SmartAdaptiveRangeSlider;
